import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCompareItems, useToggleCompareItem, useClearCompareItems } from './useCompareItems';
import { useCompareStore } from '../stores/useCompareStore';
import { useToast } from '../context/ToastContext';
import { useOfflineStatus } from './useOfflineStatus';

export const COMPARE_MAX_ITEMS = 4;

export function useCompareActions(isLoggedIn: boolean) {
  const { data: items = [] } = useCompareItems(isLoggedIn);
  const toggleMutation = useToggleCompareItem();
  const clearMutation = useClearCompareItems();
  const toast = useToast();
  const { refreshPendingCount } = useOfflineStatus();

  const migratedRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || migratedRef.current) return;
    const legacy = useCompareStore.getState().compareList;
    if (legacy.length === 0) {
      migratedRef.current = true;
      return;
    }
    if (items.length > 0) {
      useCompareStore.getState().clearCompare();
      migratedRef.current = true;
      return;
    }
    for (const id of legacy.slice(0, COMPARE_MAX_ITEMS)) {
      toggleMutation.mutate(Number(id));
    }
    useCompareStore.getState().clearCompare();
    migratedRef.current = true;
  }, [isLoggedIn, items, toggleMutation]);

  const compareList = useMemo(
    () => items.map((item) => String(item.product_id)),
    [items],
  );

  const isInCompare = useCallback(
    (id: string) => compareList.includes(id),
    [compareList],
  );

  const toggleCompare = useCallback(
    (id: string) => {
      if (!isInCompare(id) && compareList.length >= COMPARE_MAX_ITEMS) {
        toast.show(`Only ${COMPARE_MAX_ITEMS} products can be compared at once`, 'error');
        return Promise.resolve();
      }
      return toggleMutation.mutateAsync(Number(id), {
        onSuccess: () => refreshPendingCount(),
        onError: () => toast.show('Compare could not be saved', 'error'),
      });
    },
    [toggleMutation, toast, refreshPendingCount, isInCompare, compareList.length],
  );

  const addToCompare = useCallback(
    (id: string) => {
      if (isInCompare(id)) return Promise.resolve();
      return toggleCompare(id);
    },
    [isInCompare, toggleCompare],
  );

  const removeFromCompare = useCallback(
    (id: string) => {
      if (!isInCompare(id)) return Promise.resolve();
      return toggleCompare(id);
    },
    [isInCompare, toggleCompare],
  );

  const clearCompare = useCallback(() => {
    if (compareList.length === 0) return Promise.resolve();
    return clearMutation.mutateAsync(undefined, {
      onSuccess: () => refreshPendingCount(),
      onError: () => toast.show('Compare could not be cleared', 'error'),
    });
  }, [clearMutation, toast, refreshPendingCount, compareList.length]);

  const canAddMore = compareList.length < COMPARE_MAX_ITEMS;

  return {
    compareList,
    isInCompare,
    toggleCompare,
    addToCompare,
    removeFromCompare,
    clearCompare,
    canAddMore,
  };
}
